// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * Remote mutation hook backed by D1 REST + @tanstack/react-query.
 *
 * Returns `{ create, update, remove, isLoading, error }` matching
 * the local CRUD shape so consumers can switch modes transparently.
 */

import { useCallback, useState } from 'react'
import { useMutation as useTanstackMutation } from '@tanstack/react-query'
import { useCollectionMode } from '../CollectionModeContext'
import { collectionKeys } from '../../remote/queryKeys'

export interface UseRemoteMutationResult<T> {
  create: (doc: Partial<T>) => Promise<T>
  update: (id: string, updates: Partial<T>) => Promise<T>
  remove: (id: string) => Promise<boolean>
  isLoading: boolean
  error: Error | null
}

export function useRemoteMutation<T extends { id: string }>(
  collectionName: string,
): UseRemoteMutationResult<T> {
  const ctx = useCollectionMode()
  if (!ctx) throw new Error('useRemoteMutation requires CollectionModeProvider')

  const { client, queryClient } = ctx

  const createMutation = useTanstackMutation<T, Error, Partial<T>>({
    mutationFn: (doc: Partial<T>) => client.set<T>(collectionName, doc),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.all(collectionName) })
    },
  })

  const updateMutation = useTanstackMutation<T, Error, { id: string; updates: Partial<T> }>({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<T> }) =>
      client.set<T>(collectionName, { ...updates, id } as Partial<T>),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.all(collectionName) })
    },
  })

  const removeMutation = useTanstackMutation<boolean, Error, string>({
    mutationFn: (id: string) =>
      client.set<T>(collectionName, { id, _deleted: true } as unknown as Partial<T>)
        .then(() => true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.all(collectionName) })
    },
  })

  return {
    create: (doc) => createMutation.mutateAsync(doc),
    update: (id, updates) => updateMutation.mutateAsync({ id, updates }),
    remove: (id) => removeMutation.mutateAsync(id),
    isLoading: createMutation.isPending || updateMutation.isPending || removeMutation.isPending,
    error: createMutation.error ?? updateMutation.error ?? removeMutation.error ?? null,
  }
}

/**
 * Conditional variant safe to use without a CollectionModeProvider.
 *
 * Uses useState/useCallback directly (no tanstack hooks) so it maintains
 * a consistent hook count regardless of whether a provider is mounted.
 * When `collectionName` is null or no provider exists, returns no-op
 * functions that resolve immediately.
 */
export function useRemoteMutationConditional<T extends { id: string }>(
  collectionName: string | null,
): UseRemoteMutationResult<T> {
  const ctx = useCollectionMode()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const create = useCallback(async (doc: Partial<T>): Promise<T> => {
    if (!ctx || !collectionName) return {} as T
    setIsLoading(true)
    setError(null)
    try {
      const result = await ctx.client.set<T>(collectionName, doc)
      ctx.queryClient.invalidateQueries({ queryKey: collectionKeys.all(collectionName) })
      return result
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err))
      setError(e)
      throw e
    } finally {
      setIsLoading(false)
    }
  }, [ctx, collectionName])

  const update = useCallback(async (id: string, updates: Partial<T>): Promise<T> => {
    if (!ctx || !collectionName) return {} as T
    setIsLoading(true)
    setError(null)
    try {
      const result = await ctx.client.set<T>(collectionName, { ...updates, id } as Partial<T>)
      ctx.queryClient.invalidateQueries({ queryKey: collectionKeys.all(collectionName) })
      return result
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err))
      setError(e)
      throw e
    } finally {
      setIsLoading(false)
    }
  }, [ctx, collectionName])

  const remove = useCallback(async (id: string): Promise<boolean> => {
    if (!ctx || !collectionName) return false
    setIsLoading(true)
    setError(null)
    try {
      await ctx.client.set<T>(collectionName, { id, _deleted: true } as unknown as Partial<T>)
      ctx.queryClient.invalidateQueries({ queryKey: collectionKeys.all(collectionName) })
      return true
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err))
      setError(e)
      throw e
    } finally {
      setIsLoading(false)
    }
  }, [ctx, collectionName])

  return { create, update, remove, isLoading, error }
}
